import React from 'react';
import Select from "@/components/molecules/Select";
import { useState, useEffect } from "react";
import { DateRangePicker } from "@/components/atoms/DateRangePicker";
import Button from "@/components/atoms/Button";
import { FileSearch, ArrowUpDown } from "lucide-react";
import axios from "axios";
import useAccess from "@/hooks/useAccess";
import Input from "@/components/atoms/Input";
interface Option {
  id: string | number;
  label: string;
  countries: { id: string | number; name: string, status: boolean; children: { id: string | number; name: string }[] }[];
  sows: { id: string | number; name: string }[];
}

interface FilterOption {
  id: string;
  label: string;
  options: { value: string | number; label: string; countries?: { id: string | number; name: string }[]; sows?: { id: string | number; name: string }[] }[];
  selected?: (string | number)[];
}

interface LogFilterProps {
  filters: FilterOption[];
  selectedFilters: { [key: string]: (string | number)[] | null };
  onFilterChange: (filterId: string, value: string | (string | number)[] | null) => void;
  searchLogs: () => void;
  handleExport: () => void;
  exportButtonText: string;
  exportMode: boolean;
  logsCount: number;
  selectedSows: string[];
}

const LogFilter: React.FC<LogFilterProps> = ({ filters, selectedFilters, onFilterChange, searchLogs, handleExport, exportButtonText, exportMode, logsCount, selectedSows }) => {
  const [isVisibleAccountRelatedFilters, setIsVisibleAccountRelatedFilters] = useState<boolean>(false);
  const [accountRelatedOptions, setAccountRelatedOptions] = useState<any[]>([]);
  const [initialSetupDone, setInitialSetupDone] = useState(false);
  const [ticketQuery, setTicketQuery] = useState("");
  const [ticketOptions, setTicketOptions] = useState<any[]>([]);
  const [isTicketLoading, setIsTicketLoading] = useState(false);
  const { getAccess } = useAccess();

  const today = new Date();

  useEffect(() => {
    if (!initialSetupDone) {
      const defaultStartDate = formatToLocalDate(today);
      const defaultEndDate = formatToLocalDate(today);
      onFilterChange("start_date", defaultStartDate);
      onFilterChange("end_date", defaultEndDate);

      const userFilter = filters.find((filter) => filter.id === "user_ids");

      if (userFilter && userFilter.options.length === 1) {
        const singleUserOption = userFilter.options[0];
        onFilterChange("user_ids", [singleUserOption.value.toString()]);
      }
      
      setInitialSetupDone(true);
    }
  }, [initialSetupDone]);


  const handleTicketSearch = async (query: string) => {
    setIsTicketLoading(true);
    try {
      const response = await axios.get("/tickets/listing", {
        params: { search: query },
      });
      setTicketOptions(response.data.tickets || []);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setTicketOptions([]);
    } finally {
      setIsTicketLoading(false);
    }
  };

  const handleTicketInputChange = (value: string) => {
    onFilterChange("search", value);
    setTicketQuery(value);
    if (value.trim().length > 2) {
      handleTicketSearch(value);
    } else {
      setTicketOptions([]);
    }
  };

  const handleTicketSelect = (ticket: any) => {
    setTicketQuery(ticket.display_id);
    onFilterChange("search", ticket.display_id);
    setTicketOptions([]);
  };

  const handleChange = (filterId: string, value: (string | number)[]) => {
    onFilterChange(filterId, value.length ? value : null);
  
    if (value.length && filterId === 'account_ids') {
      setIsVisibleAccountRelatedFilters(true)
      const accountFilter = filters.find(filter => filter.id === 'account_ids');
      if (accountFilter) {
        const filteredOptions = accountFilter.options
          .filter(option => value.includes(option.value.toString()))
          .map(option => ({
            id: option.value,
            label: option.label,
            countries: option.countries,
            sows: option.sows
          }));
       
        setAccountRelatedOptions(filteredOptions);
      } else {
        setAccountRelatedOptions([]);
        onFilterChange('country_ids', null);
        onFilterChange('sow_ids', null);
      }
    } 
    
    if (value.length == 0 && filterId === 'account_ids'){
      setIsVisibleAccountRelatedFilters(false);
      setAccountRelatedOptions([]);
      onFilterChange('country_ids', null);
      onFilterChange('sow_ids', null);
    }
  };

  const getCountrySelectOptions = (options: Option[]) => {
    return options.flatMap(option => {
      // Check if any country in the `countries` array has non-empty `children`
      const hasChildren = option.countries.some(country => country.children.length > 0);
  
      if (!hasChildren) {
        // Case when `children` is empty: disable the option and include its `countries`
        return [
          {
            label: `${option.label} Countries`,
            value: `account-${option.id}`,
            disabled: true,
          },
          ...option.countries.map(country => ({
            label: country.name,
            value: country.id.toString(),
            disabled: false,
          }))
        ];
      }
  
      // Case when `children` is not empty: disable the option and its `countries`,
      // but include `children` as selectable options without disabling
      return [
        {
          label: `${option.label} Countries`,
          value: `account-${option.id}`,
          disabled: true,
        },
        ...option.countries.flatMap(country => [
          {
            label: `Region: ${country.name}`,
            value: country.id.toString(),
            disabled: true,
          },
          ...country.children.map(child => ({
            label: child.name,
            value: child.id.toString(),
            disabled: false,
          }))
        ])
      ];
    });
  };

  const getSowSelectOptions = (options: Option[]) => {
    console.log(options);
    return options.flatMap(option => {
      // Check if any country in the `countries` array has non-empty `children`
      return [
        {
          label: `${option.label} Sows`,
          value: `account-${option.id}`,
          disabled: true,
        },
        ...option.sows.map(sow => ({
          label: sow.name,
          value: sow.id.toString(),
          disabled: false,
        }))
      ];
    });
  };

  const formatToLocalDate = (date: Date | undefined | null) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return (
    <div className="w-full rounded-lg shadow-md">
      <div className="mt-5 w-full">
        <div className="flex">
          <div className="flex-grow">
            <div className="mb-6 mt-8 ml-2">
              <label
                className="block text-sm font-medium mb-2"
                htmlFor="ticket-id"
              >
                Ticket ID
              </label>
              <Input
                id="ticket-id"
                type="text"
                value={ticketQuery}
                placeholder="Search for a Ticket ID..."
                className="w-full md:w-1/2 px-3 py-2 border border-border rounded"
                onChange={(e) => handleTicketInputChange(e.target.value)}></Input>
              
             
              {ticketOptions.length > 0 && (
                <ul className="dark:bg-background border border-border rounded shadow-md w-6/12 max-h-60 overflow-auto z-999999">
                  {ticketOptions.map((ticket) => (
                    <li
                      key={ticket.id}
                      onClick={() => handleTicketSelect(ticket)}
                      className="px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      #{ticket.display_id} & {ticket.subject}
                    </li>
                  ))}
                </ul>
              )}
              {isTicketLoading && (
                <p className="text-gray-500 text-sm mt-2">Loading...</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 w-4/5 ml-2">
              {filters.map((filter) => (
                <div key={filter.id}>
                  <label
                    htmlFor={filter.id}
                    className="block text-sm font-medium mb-2"
                  >
                    {filter.label}:
                  </label>
                  <Select
                    multiple
                    options={
                      filter.options.map((option) => ({
                        label: option.label,
                        value: option.value.toString(),
                      })) || []
                    }
                    value={filter.selected || selectedFilters[filter.id] || []}
                    onChange={(value) => handleChange(filter.id, value)}
                    enabledSearch={true}
                    disabled={filter.id === "user_ids" && filter.options.length === 1}
                  />
                </div>
              ))}
            </div>

            {isVisibleAccountRelatedFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 w-4/5 ml-2">
                <div>
                  <label
                    htmlFor="sows"
                    className="block text-sm font-medium mb-2"
                  >
                    Sow(s)
                  </label>
                  <Select
                    multiple
                    options={getSowSelectOptions(accountRelatedOptions)}
                    value={selectedSows || selectedFilters["sow_ids"] || []}
                    onChange={(value) => onFilterChange("sow_ids", value)}
                    enabledSearch={true}
                    className="w-1/3"
                  />
                </div>
                <div>
                  <label
                    htmlFor="countries"
                    className="block text-sm font-medium mb-2"
                  >
                    Market(s)
                  </label>
                  <Select
                    multiple
                    options={getCountrySelectOptions(accountRelatedOptions)}
                    value={selectedFilters["country_ids"] || []}
                    onChange={(value) => onFilterChange("country_ids", value)}
                    enabledSearch={true}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="w-1/4 flex justify-end">
            <div className="relative z-10">
              <DateRangePicker
                onChange={(value) => {
                  const start_date = formatToLocalDate(value?.from ?? null);
                  const end_date = formatToLocalDate(value?.to ?? null);
              
                  onFilterChange("start_date", start_date);
                  onFilterChange("end_date", end_date);
                }}
                minDate={new Date(new Date().getFullYear() - 1, 0, 1)}
                maxDate={new Date(new Date().getFullYear(), 11, 31)}  
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-5 pb-5 pr-16">
          {logsCount > 0 && (
            <Button
              variant="outline"
              className="inline-flex items-center gap-2 border-border mr-4"
              onClick={handleExport}
              disabled={!getAccess(["export_logs"]) || exportMode}
            >
              <ArrowUpDown size={16} /> <span>{exportButtonText}</span>
            </Button>
          )}
            
          <Button onClick={searchLogs} className="px-4 py-2 inline-flex items-center gap-2 text-black">
            <FileSearch size={16} /> <span>Search Logs</span>
          </Button>

        </div>
      </div>
    </div>
  );
};

export default LogFilter;